import React, { useRef, useState } from 'react';
import { Button, Form } from 'semantic-ui-react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import sendToast from '../../../utils/Toast';
import {
    confirmResidentPaymentMethodSetup,
    initializeResidentPaymentMethodSetup,
} from '../../services/Payments';
import './style.less';

// Stripe SetupIntent card-capture form. Must be rendered inside a
// @stripe/react-stripe-js <Elements> provider (see payments/index.tsx and
// neighbors-greetings/GreetingsAddonCard.tsx for the two current call sites).
// Extracted so both flows share one card-capture implementation.
type AddCardFormProps = {
    residentId: string;
    facilityId: string;
    shouldMakeDefault: boolean;
    replacingMethodId?: string;
    // Wording overrides for flows that frame the card differently (the Greetings sign-up);
    // the Payments page keeps the defaults.
    nameLabel?: string;
    submitLabel?: string;
    footnote?: React.ReactNode;
    // The caller cannot go ahead yet (e.g. the price it is about to charge is unknown): Save stays off.
    submitDisabled?: boolean;
    onCancel: () => void;
    // Called once the card is saved (and, when replacing, the old one is dealt with). Must not throw.
    onSuccess: () => Promise<void>;
    // Lets the page block closing the dialog and other card actions while the card is being saved.
    onSubmittingChange?: (submitting: boolean) => void;
};

const AddCardForm: React.FC<AddCardFormProps> = ({
    residentId,
    facilityId,
    shouldMakeDefault,
    replacingMethodId,
    nameLabel = 'Cardholder name',
    submitLabel = 'Save card',
    footnote,
    submitDisabled = false,
    onCancel,
    onSuccess,
    onSubmittingChange,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [holderName, setHolderName] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    // Synchronous guard: a second click can land before `submitting` has re-rendered, and a second
    // save would add the card twice.
    const submitInFlightRef = useRef<boolean>(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) {
            sendToast('warn', 'Payments form is still loading. Please try again.');
            return;
        }
        if (!holderName.trim()) {
            sendToast('warn', 'Please enter the cardholder name');
            return;
        }

        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;

        let cardSaved = false;
        try {
            setSubmitting(true);
            onSubmittingChange?.(true);
            const setup = await initializeResidentPaymentMethodSetup(residentId, facilityId);
            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                throw new Error('Card input not ready');
            }

            const confirmResult = await stripe.confirmCardSetup(setup.setupToken, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: holderName.trim(),
                    },
                },
            });

            if (confirmResult.error) {
                throw new Error(confirmResult.error.message || 'Unable to confirm card setup');
            }

            const setupConfirmationId =
                (confirmResult.setupIntent && confirmResult.setupIntent.id)
                    ? confirmResult.setupIntent.id
                    : setup.setupConfirmationId;

            // One call: the server saves the card and, when replacing, makes it the default and
            // removes the old card. The response says if it could not remove the old one.
            const saved = await confirmResidentPaymentMethodSetup(
                residentId,
                setupConfirmationId,
                facilityId,
                shouldMakeDefault,
                replacingMethodId,
            );
            cardSaved = true;
            // Anything but a confirmed removal is a warning, so "Card replaced" never shows while the
            // old card may still be there.
            if (replacingMethodId && saved?.replacedMethodRemoved !== true) {
                sendToast('warn', saved?.message || 'New card saved, but the old card was not removed. You can remove it from the list.');
            } else {
                sendToast('success', replacingMethodId ? 'Card replaced' : 'Card added successfully');
            }
            await onSuccess();
        } catch (error) {
            // Once the card is saved nothing below may report a failed add.
            if (!cardSaved) {
                sendToast('error', error instanceof Error ? error.message : (replacingMethodId ? 'Unable to replace card' : 'Unable to add card'));
            }
        } finally {
            submitInFlightRef.current = false;
            onSubmittingChange?.(false);
            setSubmitting(false);
        }
    };

    return (
        <Form>
            <Form.Input
                label={nameLabel}
                required
                value={holderName}
                placeholder="Name on card"
                onChange={(e) => setHolderName(e.target.value)}
            />
            <Form.Field>
                <label>Card details</label>
                <div className="add-card-form-card-element">
                    <CardElement
                        options={{
                            hidePostalCode: true,
                            style: {
                                base: {
                                    fontFamily: '"DM Sans", system-ui, sans-serif',
                                    fontSize: '15px',
                                    color: '#1B2533',
                                    '::placeholder': { color: '#9AA8BC' },
                                },
                                invalid: {
                                    color: '#C0362C',
                                },
                            },
                        }}
                    />
                </div>
            </Form.Field>
            {footnote}
            <div className="add-card-form-actions">
                <Button className="add-card-form-btn-ghost" type="button" onClick={onCancel} disabled={submitting}>Cancel</Button>
                <Button className="add-card-form-btn-primary" primary type="button" onClick={handleSubmit} loading={submitting} disabled={submitting || submitDisabled || !stripe || !holderName.trim()}>
                    {submitLabel}
                </Button>
            </div>
        </Form>
    );
};

export default AddCardForm;
